import PropTypes from 'prop-types';
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import I18n from 'i18n-js';
import { Input } from 'grab-ui';

import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import { deliveriesFromPropsSelector } from 'selectors/new-booking-selectors';
import {
  bulkUploadFetchingPromoSelector,
  bulkUploadOfferListSelector,
  bulkUploadOfferConfigSelector,
} from 'selectors/new-bulk-upload-selectors';

import OfferTermCondition from './offer-term-condition';
import OfferListComponent from './offer-list-component';
import PromoCodeInputComponent from './promo-code-input-component';
import OfferListFooter from './offer-list-footer';
import OverlayBody from '../overlay-body';
import styles from './styles.less';
import OfferPanelContext from './offer-panel-context';
import { useHandleScribeOffers } from '../../common/scribe-offers-common';

const BulkOfferListOverLay = ({
  dispatch,
  offerList,
  fetchingPromo,
  offerConfig,
  onClose,
}) => {
  const [offerTC, setOfferTC] = useState(null);
  const [showOfferDetail, setShowOfferDetail] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const selectedOfferID = _get(selectedOffer, 'offerID', null);

  useEffect(
    () => {
      setSelectedOffer(null);
      if (offerConfig && offerConfig.offerID) {
        const offer = offerList.find(
          offer => offer.offerID === offerConfig.offerID,
        );
        if (offer) {
          setSelectedOffer(offer);
        }
      }
    },
    [offerList, offerConfig],
  );

  const handleViewOfferDetail = useCallback(
    offerID => () => {
      const chooseOffer = offerList.find(offer => offer.offerID === offerID);

      if (chooseOffer) {
        setOfferTC(chooseOffer);
        setShowOfferDetail(true);
      }
    },
    [offerList],
  );

  const onUseOffer = useCallback(
    offerID => () => {
      const offer = offerList.find(offer => offer.offerID === offerID);
      if (offer) {
        setSelectedOffer(offer);
      }
      onCloseOfferDetail();
    },
    [dispatch, offerList, onCloseOfferDetail],
  );

  const onRemoveOffer = useCallback(
    () => {
      setSelectedOffer(null);
      onCloseOfferDetail();
    },
    [dispatch, onCloseOfferDetail],
  );

  const setOfferConfig = useCallback(
    offer => {
      dispatch(NewBulkUploadDuck.creators.trySetOfferConfig(offer));
    },
    [dispatch],
  );

  const onCloseOfferDetail = useCallback(
    () => {
      setShowOfferDetail(false);
      setOfferTC(null);
    },
    [setShowOfferDetail, setOfferTC],
  );

  const onPromoCodeFocus = useCallback(
    () => {
      dispatch(NewBulkUploadDuck.creators.updateOfferList([]));
      setSelectedOffer(null);
      setShowCodeInput(true);
    },
    [setShowCodeInput, dispatch],
  );

  const onClosePromoCodeInput = useCallback(
    () => {
      setShowCodeInput(false);
      setSelectedOffer(offerConfig);
    },
    [setShowCodeInput, dispatch, offerConfig],
  );

  const onCloseOverlay = useCallback(
    () => {
      setSelectedOffer(null);
      onClose();
    },
    [dispatch, onClose],
  );

  const onSearchTextChange = useCallback(
    _debounce(promoCode => {
      dispatch(NewBulkUploadDuck.creators.inputPromoCode(promoCode));
    }, 300),
    [dispatch],
  );

  const handleSelectOffer = useCallback(
    offerID => {
      if (selectedOffer && selectedOffer.offerID === offerID) {
        setSelectedOffer(null);
        return;
      }
      const offer = offerList.find(offer => offer.offerID === offerID);
      if (offer) {
        setSelectedOffer(offer);
      }
    },
    [dispatch, offerList, selectedOffer],
  );

  const errorMessage = useMemo(() => null, []);

  const {
    handleScribeSelectOffer,
    handleScribeOfferDetailDefault,
    handleScribeApplyOffer,
    handleScribeContinueWithoutOffer,
    handleScribeOfferDetailBack,
    handleScribeOfferDetailUseOffer,
    handleScribeEnterCodeDetailsBack,
    handleScribeEnterCodeDetailsTypeCode,
    handleScribeEnterCodeDetailsShowErrorResult,
  } = useHandleScribeOffers(offerConfig);

  const offerPanelContext = useMemo(
    () => ({
      onViewOfferDetail: handleViewOfferDetail,
      handleSelectOffer,
      setOfferConfig,
      errorMessage,
      // Scribe Offer
      handleScribeSelectOffer,
      handleScribeApplyOffer,
      handleScribeContinueWithoutOffer,
      handleScribeOfferDetailDefault,
      handleScribeOfferDetailBack,
      handleScribeOfferDetailUseOffer,
      handleScribeEnterCodeDetailsBack,
      handleScribeEnterCodeDetailsTypeCode,
      handleScribeEnterCodeDetailsShowErrorResult,
    }),
    [handleViewOfferDetail, handleSelectOffer, setOfferConfig, errorMessage],
  );

  if (showOfferDetail) {
    return (
      <OfferPanelContext.Provider value={offerPanelContext}>
        <OfferTermCondition
          offer={offerTC}
          selectedOfferID={selectedOfferID}
          onClose={onCloseOfferDetail}
          onSelectOffer={onUseOffer(offerTC.offerID)}
          onRemoveOffer={onRemoveOffer}
        />
      </OfferPanelContext.Provider>
    );
  }

  if (showCodeInput) {
    return (
      <OfferPanelContext.Provider value={offerPanelContext}>
        <PromoCodeInputComponent
          offerList={offerList}
          fetchingPromo={fetchingPromo}
          selectedOffer={selectedOffer}
          offerConfig={offerConfig}
          onClose={onClosePromoCodeInput}
          onCloseOverlay={onCloseOverlay}
          onUseOffer={onUseOffer}
          onRemoveOffer={onRemoveOffer}
          onSearchTextChange={onSearchTextChange}
        />
      </OfferPanelContext.Provider>
    );
  }

  return (
    <OfferPanelContext.Provider value={offerPanelContext}>
      <OverlayBody
        title={I18n.t('phrase.offers.mktplace_offers_body_item_offers')}
        showHeader
        showClose
        showTitle
        onClose={onCloseOverlay}
        showFooter
        showConfirm={false}
        footer={
          <OfferListFooter
            onClose={onClose}
            offerList={offerList}
            selectedOffer={selectedOffer}
            offerConfig={offerConfig}
          />
        }
        classnames={{
          footer: styles.footer,
        }}
      >
        <div className={styles.promoListContainer}>
          <Input
            placeholder={I18n.t(
              'phrase.offers.MKTPLACE_OFFERS_ENTER_PROMO_CODE',
            )}
            onFocus={onPromoCodeFocus}
          />
          <OfferListComponent
            offerList={offerList}
            selectedOffer={selectedOffer}
            offerConfig={offerConfig}
            fetchingPromo={fetchingPromo}
          />
        </div>
      </OverlayBody>
    </OfferPanelContext.Provider>
  );
};

BulkOfferListOverLay.defaultProps = {
  offerConfig: null,
};

BulkOfferListOverLay.propTypes = {
  dispatch: PropTypes.func.isRequired,
  offerList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fetchingPromo: PropTypes.bool.isRequired,
  offerConfig: PropTypes.shape({}),
  onClose: PropTypes.func.isRequired,
};
export default connect((state, props) => ({
  deliveries: deliveriesFromPropsSelector(state, props),
  offerList: bulkUploadOfferListSelector(state),
  fetchingPromo: bulkUploadFetchingPromoSelector(state),
  offerConfig: bulkUploadOfferConfigSelector(state),
}))(BulkOfferListOverLay);
